import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

import UiContext from 'common/UiContext/UiContext';
import AuthContext from 'common/auth';
import useApi from 'common/useApi';

import IFilters from './IFilter';

import DataTable from 'components/DataTable';
import displayHashrateValue from 'common/displayHashrateValue';
import IDatatableEntry from 'common/types/IDatatableEntry';
import DataTablePagination from 'components/DataTablePagination';
import Filters from './Filters';
import ISortFilter from 'common/types/ISortFilter';
import classNames from 'classnames';

type IRawData = {
  alert_limit: string
  hashrate: string
  hashrate_1h: string
  id: number
  label: string
  account_name: string
  name: string
  scoring_hashrate: string
  status: IDatatableEntry
  warranty_health: string
}

const WorkersTable = () => {
  const { user } = useContext(AuthContext)!;
  const [searchParams] = useSearchParams();
  // Seed the State filter from the URL so the low-hashrate alert banner's
  // "View" link (/workers?status=low) lands on the filtered table.
  const statusParam = searchParams.get('status') ?? '';
  const [checkedIds, setCheckedIds] = useState<Array<number>>([]);
  const [filters, setFilters] = useState<IFilters>({
    label: '',
    ordering: 'name',
    page: 1,
    search: null,
    status: statusParam,
  });
  const [sortBy, setSortBy] = useState<ISortFilter>({
    asc: true,
    headerKey: 1,
  });
  const { addToast } = useContext(UiContext);
  const { data, isError, isLoading, isFetching, refetch } = useApi('getList', '/api/workers', filters);
  const ref = useRef(null);

  const headers = [
    'Worker Name',
    'State',
    'Scoring HR',
    'HR (5 min)',
    'HR (1 hour)',
    'Alert Limit',
    'Label',
    'Warranty Health',
    '',
  ];

  if (user?.is_superuser) {
    headers.unshift('Account');
  }

  const sortEntries = (headerKey: number) => {
    setSortBy((old) => ({
      asc: old.headerKey === headerKey ? !old.asc : true,
      headerKey: headerKey,
    }));
  };

  useEffect(() => {
    const headers = [
      'name',
      'status',
      'scoring_hashrate',
      'hashrate',
      'hashrate_1h',
      'alert_limit',
      'label',
      'warranty_health',
    ];

    if (user?.is_superuser) {
      headers.unshift('account_name');
    }

    setFilters((old) => ({
      ...old,
      ordering: `${sortBy.asc ? '' : '-'}${headers[sortBy.headerKey]}`,
    }));
  }, [sortBy]);

  useEffect(() => {
    if(isError) {
        addToast!('Error loading data. Please, retry');
    }
  }, [isError]);

  // Follow ?status= changes that happen while the table is already mounted
  // (e.g. clicking the alert banner from the workers page itself).
  useEffect(() => {
    setFilters((old) => (old.status === statusParam ? old : { ...old, page: 1, status: statusParam }));
  }, [statusParam]);

  const parseData = () => data?.results.map(({
    id,
    account_name,
    name,
    status,
    scoring_hashrate,
    hashrate,
    hashrate_1h,
    alert_limit,
    label,
    warranty_health,
  }: IRawData) => {
    const row = [
      id,
      name || '(auto)',
      {
        entry: status,
        formatter: 'WorkerStateLabel',
      },
      `${Object.values(displayHashrateValue(parseFloat(scoring_hashrate))).join(' ')}h/s`,
      `${Object.values(displayHashrateValue(parseFloat(hashrate))).join(' ')}h/s`,
      `${Object.values(displayHashrateValue(parseFloat(hashrate_1h))).join(' ')}h/s`,
      `${Object.values(displayHashrateValue(parseFloat(alert_limit))).join(' ')}h/s`,
      {
        entry: label,
        formatter: 'WorkerLabels',
      },
      {
        entry: warranty_health,
        formatter: 'WorkerWarrantyHealth',
      },
      {
        entry: id,
        formatter: 'WorkerDetailLink',
      },
    ];

    if (user?.is_superuser) {
      row.splice(1, 0, account_name);
    }

    return row;
  });

  return (
    <SwitchTransition>
      <CSSTransition
        nodeRef={ref}
        classNames='fade'
        timeout={300}
        mountOnEnter={true}
        unmountOnExit={true}
        key={isLoading ? 1 : 0}
      >

        <article
          className={classNames({
            box: true,
            isLoading,
            table: true,
          })}
          ref={ref}
        >
          <Filters
            refetch={refetch}
            checkedIds={checkedIds}
            setCheckedIds={setCheckedIds}
            filters={filters}
            setFilters={setFilters}
          />
          <DataTable
            headers={headers}
            content={parseData() ?? []}
            checkedIds={checkedIds}
            isLoading={isLoading || isFetching}
            setCheckedIds={setCheckedIds}
            sortBy={sortBy}
            sortFunction={sortEntries}
            withCheckboxes
            sortable
          />
          <DataTablePagination
            page={filters.page}
            totalItems={data?.count ?? 0}
            setPage={(page: number) => {
              setFilters((old) => ({
                ...old,
                page,
              }));
            }}
          />
        </article>
      </CSSTransition>
    </SwitchTransition>
  );
};

export default WorkersTable;
